import { useCallback, useEffect, useMemo, useRef } from 'react';

import isEmpty from 'lodash/isEmpty';
import { graphql, useFragment } from 'react-relay';

import { RequestStatus } from '../../constants';
import type {
	fnGetFilteredProductLinks,
	GlobalShortcutsItem,
	ProductLink,
	ShortcutsConfig,
} from '../../types';

import type { useGlobalShortcutsDataRelay$key } from './__generated__/useGlobalShortcutsDataRelay.graphql';
import type { InferShortcutsOutput, UseGlobalShortcutDataInputBase } from './types';

export type UseGlobalShortcutsDataRelayFragmentInput<T> = UseGlobalShortcutDataInputBase & {
	queryRef: useGlobalShortcutsDataRelay$key;
	shortcutsConfig: T;
};

/**
 * Reads the colocated Relay fragment on `Query` and merges backend-provided available product URLs into shortcut config.
 * Parent operations should gate this spread with `@include(if: …)` when appSwitcher_availableProductsBySite query should be skipped.
 */
export const useGlobalShortcutsDataRelay = <
	T extends ShortcutsConfig | GlobalShortcutsItem[] | undefined = undefined,
>({
	queryRef,
	cloudId,
	onError,
	shortcutsConfig,
	onUpdateGetFilteredProductLinks,
}: UseGlobalShortcutsDataRelayFragmentInput<T>): InferShortcutsOutput<T> => {
	const onUpdateGetFilteredProductLinksRef = useRef(onUpdateGetFilteredProductLinks);
	onUpdateGetFilteredProductLinksRef.current = onUpdateGetFilteredProductLinks;
	/* eslint-disable @atlassian/relay/graphql-naming */
	/* eslint-disable @atlassian/relay/no-wrapping-hooks */
	const fragmentData = useFragment(
		graphql`
			fragment useGlobalShortcutsDataRelay on Query {
				appSwitcher_availableProductsBySite(cloudId: $cloudId) {
					__typename
					... on AppSwitcherAvailableProductsBySite {
						products {
							__typename
							... on AppSwitcherSingleUrlProduct {
								productType
								url
							}
							... on AppSwitcherSiteScopedProduct {
								productType
								sites {
									siteId
									url
								}
							}
						}
					}
					... on QueryError {
						message
					}
				}
			}
		`,
		queryRef,
	);
	/* eslint-enable @atlassian/relay/graphql-naming */
	/* eslint-enable @atlassian/relay/no-wrapping-hooks */

	const appSwitcherResult = fragmentData.appSwitcher_availableProductsBySite;

	useEffect(() => {
		if (appSwitcherResult?.__typename === 'QueryError') {
			onError?.(
				new Error(appSwitcherResult.message ?? 'appSwitcher_availableProductsBySite failed'),
			);
		}
	}, [appSwitcherResult, onError]);

	const { productLinksByProductType, productLinksRequestStatus } = useMemo(() => {
		const productLinksRequestStatus: RequestStatus | undefined = !appSwitcherResult
			? RequestStatus.INIT
			: appSwitcherResult.__typename === 'QueryError'
				? RequestStatus.ERROR
				: appSwitcherResult.__typename === 'AppSwitcherAvailableProductsBySite'
					? RequestStatus.COMPLETE
					: RequestStatus.INIT;

		const productLinksByProductType: Map<string, ProductLink[]> = new Map();
		if (appSwitcherResult?.__typename === 'AppSwitcherAvailableProductsBySite') {
			const products = appSwitcherResult.products;
			if (products?.length) {
				for (const product of products) {
					if (product.__typename === 'AppSwitcherSingleUrlProduct') {
						if (product.url) {
							productLinksByProductType.set(product.productType, [{ url: product.url }]);
						}
					} else if (product.__typename === 'AppSwitcherSiteScopedProduct') {
						const links = (product.sites ?? [])
							.filter((site) => Boolean(site.url))
							.map((site) => ({ cloudId: site.siteId, url: site.url }) as ProductLink);
						if (links.length) {
							productLinksByProductType.set(product.productType, links);
						}
					}
				}
			}
		}
		return { productLinksByProductType, productLinksRequestStatus };
	}, [appSwitcherResult]);

	const getRelayProductLinksForKey = useCallback<fnGetFilteredProductLinks>(
		(apsKey: string) => ({
			links: productLinksByProductType.get(apsKey) ?? [],
			status: productLinksRequestStatus,
		}),
		[productLinksByProductType, productLinksRequestStatus],
	);

	useEffect(() => {
		if (onUpdateGetFilteredProductLinksRef.current) {
			onUpdateGetFilteredProductLinksRef.current(getRelayProductLinksForKey);
		}
	}, [getRelayProductLinksForKey]);

	const productUrlMap = useMemo(() => {
		const urlByProductType: Map<string, string> = new Map();
		for (const [productType, links] of productLinksByProductType.entries()) {
			if (!links.length) {
				continue;
			}
			const match = cloudId ? links.find((l) => l.cloudId === cloudId) : undefined;
			const chosen = match ?? links[0];
			if (chosen?.url) {
				urlByProductType.set(productType, chosen.url);
			}
		}
		return urlByProductType;
	}, [productLinksByProductType, cloudId]);

	const appShortcuts = useMemo(() => {
		if (isEmpty(shortcutsConfig)) {
			return shortcutsConfig;
		}

		const config = shortcutsConfig as ShortcutsConfig | GlobalShortcutsItem[];

		if (!productUrlMap.size) {
			return shortcutsConfig;
		}

		const isArray = Array.isArray(config);

		if (isArray) {
			return config.reduce<GlobalShortcutsItem[]>((acc, item) => {
				if (item.type !== 'link' || !item.apsKey) {
					acc.push(item);
					return acc;
				}

				const url = productUrlMap.get(item.apsKey);
				if (url) {
					acc.push({
						...item,
						url,
					});
				}
				return acc;
			}, []);
		}

		const entries = Object.entries(config);
		return entries.reduce<ShortcutsConfig>((acc, [key, value]) => {
			if (value.type !== 'link' || !value.apsKey) {
				acc[key] = value;
				return acc;
			}

			const url = productUrlMap.get(value.apsKey);
			if (url) {
				acc[key] = {
					...value,
					url,
				};
			}
			return acc;
		}, {});
	}, [productUrlMap, shortcutsConfig]);

	return appShortcuts as InferShortcutsOutput<T>;
};
